import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import CampaignEmailCreateStep1 from '@/views/campaignEmail/CampaignEmailCreate/CampaignEmailCreateStep1.vue'
import CampaignEmailCreateStep2 from '@/views/campaignEmail/CampaignEmailCreate/CampaignEmailCreateStep2.vue'
import CampaignEmailCreateStep3 from '@/views/campaignEmail/CampaignEmailCreate/CampaignEmailCreateStep3.vue'

import type { SelectedAudience } from '@/components/audiences/Audience.vue'
import type { Step } from '@/components/base/BaseStepper.vue'
import type { ContentScope } from '@/types/contentScope'

dayjs.extend(customParseFormat)

export default function useCampaignEmailForm() {
  const { t } = useI18n()
  const { fetchEstimateAudience, formatAudienceRulesRecordToArray } = useAudience()
  const { listTemplates, getEmailContent } = useEmailContent()

  const count = ref(0)
  const isLoadingCount = ref(false)
  const name = ref('')
  const scope = ref<ContentScope | null>(null)
  const step = ref(1)
  const emailContentId = ref<number | null>(null)
  const conversionDelay = ref(7)
  const launchChoice = ref('now')
  const dateCampaign = ref<string | null>(null)
  const timeCampaign = ref<{ hour: number | null; minute: number | null }>({
    hour: null,
    minute: null
  })

  const selectedAudience = ref<SelectedAudience>({
    availableLists: [],
    lists: [],
    excludedLists: [],
    stores: []
  })

  const steppers = reactive({
    1: shallowRef(CampaignEmailCreateStep1),
    2: shallowRef(CampaignEmailCreateStep2),
    3: shallowRef(CampaignEmailCreateStep3)
  })

  const steps = computed<Step[]>(() => [
    {
      title: t('pages.campaignEmail.create.steps.step1Title'),
      description: t('pages.campaignEmail.create.steps.step1Description'),
      disabled: false
    },
    {
      title: t('pages.campaignEmail.create.steps.step2Title'),
      description: t('pages.campaignEmail.create.steps.step2Description'),
      disabled: name.value === '' || count.value === 0
    },
    {
      title: t('pages.campaignEmail.create.steps.step3Title'),
      description: t('pages.campaignEmail.create.steps.step3Description'),
      disabled: !emailContentId.value
    }
  ])

  const allTemplates = ref<{ id: string; name: string }[]>([])
  const selectedTemplateName = computed(() => {
    const found = allTemplates.value.find((tpl) => Number(tpl.id) === emailContentId.value)
    return found?.name ?? null
  })

  const emailContentSubject = ref('')
  const emailContentPreviewText = ref('')

  const fetchEmailContentMetadata = async () => {
    if (!emailContentId.value) {
      emailContentSubject.value = ''
      emailContentPreviewText.value = ''
      return
    }
    try {
      const content = await getEmailContent(String(emailContentId.value))
      emailContentSubject.value = content.structure?.subject || ''
      emailContentPreviewText.value = content.structure?.previewText || ''
    } catch {
      emailContentSubject.value = ''
      emailContentPreviewText.value = ''
    }
  }

  const fetchAllTemplates = async () => {
    try {
      allTemplates.value = await listTemplates()
    } catch {
      // handled in step 2
    }
  }

  const userStoresAudience = ref(0)
  const userStoresCount = ref(0)
  const fetchUserStoresAudience = async () => {
    try {
      const audienceRulesFormatted = formatAudienceRulesRecordToArray({})
      const response = await fetchEstimateAudience(audienceRulesFormatted, [], undefined)
      userStoresAudience.value = response.userStoresAudience
      userStoresCount.value = response.userStoresCount
    } catch (error) {
      console.error(error)
    }
  }

  const buildLaunchDate = () => {
    if (launchChoice.value === 'now') return null
    if (!dateCampaign.value || timeCampaign.value.hour === null || timeCampaign.value.minute === null) return null

    return dayjs(dateCampaign.value, 'DD/MM/YYYY')
      .hour(timeCampaign.value.hour)
      .minute(timeCampaign.value.minute)
      .second(0)
      .format()
  }

  watch(emailContentId, () => {
    fetchAllTemplates()
    fetchEmailContentMetadata()
  })

  return {
    count,
    isLoadingCount,
    name,
    scope,
    step,
    emailContentId,
    conversionDelay,
    launchChoice,
    dateCampaign,
    timeCampaign,
    selectedAudience,
    allTemplates,
    emailContentSubject,
    emailContentPreviewText,
    userStoresAudience,
    userStoresCount,
    steppers,
    steps,
    selectedTemplateName,
    buildLaunchDate,
    fetchAllTemplates,
    fetchEmailContentMetadata,
    fetchUserStoresAudience
  }
}
